import { useCallback, useState } from "react";
import { Checkbox, Icon } from "@mui/material";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { deactivateSKU } from "services/data.internal.service";
import { ListLayout, TableCard } from "ui-components/Layout";
import { EditButton, DelButton, MoreButton } from "ui-components/IconButton";

import { useCustomerValue } from "providers/CustomerContext";
import {
  getSKUList,
  getSKUStatusCount,
  activeSKU,
  batchDeactivateSKU,
} from "services/data.internal.service";
import ServerTable from "ui-components/tables/ServerTable";
import { Success, Error_, MessageDialog } from "ui-components/Alert";
import BasicTabs from "ui-components/BasicTabs";
import SKULog from "./components/SKULog";
import { ActiveButton } from "ui-components/IconButton";
import SKUKitLog from "./components/SKUKitLog";
import { LoadingDialog } from "ui-components/Alert";
import { useTeamValue } from "providers/TeamContext";
import { exportSKU } from "services/data.internal.service";
import { downloadFile } from "layouts/views/common/file";
import { getFileName } from "layouts/views/common/file";

function SKUList() {
  const navigate = useNavigate();
  const { permissions, allowAll } = useTeamValue();
  const { customerCode } = useCustomerValue();
  const [status, setStatus] = useState("Active");
  const [tabs, setTabs] = useState([
    { label: "Active", count: 0 },
    { label: "InActive", count: 0 },
  ]);
  const [alert, setAlert] = useState(null);
  const [dialog, setDialog] = useState(null);

  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10 });
  const [dataInfo, setDataInfo] = useState({ totalRecord: 0, pageCount: 0 });
  const [columnFilters, setColumnFilters] = useState([]);
  const [sorting, setSorting] = useState([]);
  const [data, setData] = useState([]);
  const queryClient = useQueryClient();
  const [selectedIds, setSelectedIds] = useState([]);

  const active = status === "Active" ? true : false;
  const onError = (data) => setAlert(<Error_ data={data} onChange={setAlert} />);
  const onSuccess = (data) => {
    queryClient.invalidateQueries(["sku-list"]);
    setAlert(<Success data={data} onChange={setAlert} />);
  };

  const addPermission =
    allowAll ||
    (Boolean(permissions["SKU"]) && permissions["SKU"].findIndex((b) => b === "new") !== -1);
  const editPermission =
    allowAll ||
    (Boolean(permissions["SKU"]) && permissions["SKU"].findIndex((b) => b === "edit") !== -1);
  const deactivatePermission =
    allowAll ||
    (Boolean(permissions["SKU"]) && permissions["SKU"].findIndex((b) => b === "deactivate") !== -1);

  const vendorSKUAccess =
    allowAll ||
    (Boolean(permissions["Vendor SKU"] || Boolean(permissions["Retail Barcode"])));
  const packTypeAccess =
    allowAll ||
    (Boolean(permissions["SKU Pack Type"]));

  const tableProps = {
    pagination,
    setPagination,
    columnFilters,
    setColumnFilters,
    sorting,
    setSorting,
    dataInfo,
  };
  useQuery({
    queryKey: ["sku-list", "status", { customerCode }],
    queryFn: () => getSKUStatusCount({ customerCode }),
    enabled: Boolean(customerCode),
    onSuccess: (data) => setTabs(data.data.data),
    onError,
  });
  useQuery({
    queryKey: ["sku-list", { customerCode, pagination, columnFilters, sorting, status, active }],
    queryFn: () => getSKUList({ customerCode, active, pagination, columnFilters, sorting }),
    onSuccess: (data) => {
      const { data: rows, totalRecord, pageCount } = data.data;
      setData(rows);
      setDataInfo({ totalRecord, pageCount });
    },
    enabled: Boolean(customerCode),
    keepPreviousData: true,
  });
  const delMutation = useMutation(deactivateSKU, {
    onSuccess,
    onError,
  });
  const batchDelMutation = useMutation(batchDeactivateSKU, {
    onSuccess,
    onError,
  });
  const activeMutation = useMutation(activeSKU, {
    onSuccess,
    onError,
  });
  const exportMutation = useMutation(exportSKU, {
    onSuccess: (response) => {
      downloadFile(response.data, `${getFileName("SKU", customerCode)}.xlsx`);
    },
    onError,
  });
  const onEdit = (req) => {
    if (req.kitType === "KIT") navigate("/setup/sku/kit/edit", { state: req });
    else navigate("/setup/sku/edit", { state: req });
  };
  const onDelete = (req) => {
    setDialog(
      <MessageDialog
        title={`Deactivate ${req.skuCode}`}
        onClose={() => setDialog(null)}
        onOk={() => {
          delMutation.mutate({ ...req, skuCodes: [req.skuCode] });
          setDialog(null);
        }}
      />
    );
  };
  const onBatchDelete = () => {
    setDialog(
      <MessageDialog
        title={`Deactivate All SKU (Out Of Stock)?`}
        onClose={() => setDialog(null)}
        onOk={() => {
          batchDelMutation.mutate({ customerCode, skuCodes: [] });
          setDialog(null);
        }}
      />
    );
  };
  const onActive = (req) => {
    setDialog(
      <MessageDialog
        title={`Activate ${req.skuCode}`}
        onClose={() => setDialog(null)}
        onOk={() => {
          activeMutation.mutate({ ...req, skuCodes: [req.skuCode] });
          setDialog(null);
        }}
      />
    );
  };
  const [logRequest, setLogRequest] = useState(null);
  const [kitLogRequest, setKitLogRequest] = useState(null);
  const [open, setOpen] = useState(false);
  const [kitOpen, setKitOpen] = useState(false);
  const logProp = { open, setOpen, logRequest, setLogRequest };
  const kitLogProp = {
    open: kitOpen,
    setOpen: setKitOpen,
    logRequest: kitLogRequest,
    setLogRequest: setKitLogRequest,
  };

  const handleChange = useCallback((data) => {
    setSelectedIds(data);
  }, []);

  const onBatch = () => {
    const req = { customerCode, skuCodes: selectedIds.map((x) => x.skuCode) };
    if (active) {
      delMutation.mutate(req);
      setDialog(null);
    } else {
      activeMutation.mutate(req);
      setDialog(null);
    }
  };

  const columns = [
    {
      id: "select",
      header: ({ table }) => (
        <Checkbox
          sx={{
            padding: 0,
            "& .MuiSvgIcon-root": {
              borderColor: "#97b8d8",
            },
          }}
          checked={table.getIsAllRowsSelected()}
          indeterminate={table.getIsSomeRowsSelected()}
          onChange={table.getToggleAllRowsSelectedHandler()}
        />
      ),
      cell: ({ row }) => (
        <Checkbox
          sx={{
            padding: 0,
            "& .MuiSvgIcon-root": {
              borderColor: "#97b8d8",
            },
          }}
          checked={row.getIsSelected()}
          disabled={!row.getCanSelect()}
          indeterminate={row.getIsSomeSelected()}
          onChange={row.getToggleSelectedHandler()}
        />
      ),
    },
    {
      header: "SKU Code",
      accessorKey: "skuCode",
      cell: ({ row }) => {
        const { customerCode, skuCode, kitType } = row.original;
        return (
          <span
            style={{ color: "#1A73E8", cursor: "pointer", textDecoration: "underline" }}
            onClick={() => {
              if (kitType === "SINGLE") {
                setOpen(true);
                setLogRequest({ skuCode, customerCode });
              } else {
                setKitOpen(true);
                setKitLogRequest({ skuCode, customerCode });
              }
            }}
          >
            {skuCode}
          </span>
        );
      },
    },
    { header: "Description", accessorKey: "description" },
    { header: "Category", accessorKey: "category" },
    { header: "Picking Rules", accessorKey: "pickingRules" },
    { header: "Kit Type", accessorKey: "kitType" },
    {
      header: "Action",
      accessorKey: "action",
      enableColumnFilter: false,
      cell: ({ row }) => {
        const { customerCode, skuCode, kitType } = row.original;
        const state = { customerCode, skuCode };
        const optionsArray = []
        if (vendorSKUAccess) optionsArray.push({
                    label: "Vendor SKU",
                    icon: "search",
                    onClick: () => navigate("/setup/sku/vendor-sku", { state }),
                  });
        if (packTypeAccess) optionsArray.push({
                    label: "SKU Pack Type",
                    icon: "search",
                    onClick: () => navigate("/setup/sku/pack-type", { state }),
                  })
        if (active) {
          return (
            <>
              <EditButton
                title="edit sku"
                disabled={!editPermission}
                onClick={() => onEdit({ ...state, kitType })}
              />
              <DelButton
                title="deactivate sku"
                disabled={!deactivatePermission}
                onClick={() => onDelete(state)}
              />
              <MoreButton
                options={optionsArray}
              />
            </>
          );
        } else {
          return (
            <ActiveButton
              title="activate sku"
              disabled={!editPermission}
              onClick={() => onActive(state)}
            />
          );
        }
      },
    },
  ];
  const batchDeleteStatus = batchDelMutation.status === "loading";
  const deleteStatus = delMutation.status === "loading";
  const activeStatus = activeMutation.status === "loading";
  const exportStatus = exportMutation.status === "loading";
  return (
    <ListLayout>
      <SKULog {...logProp} />
      <SKUKitLog {...kitLogProp} />
      {(batchDeleteStatus || deleteStatus || activeStatus || exportStatus) && <LoadingDialog />}
      {dialog}
      <MDBox my={3}>
        {alert}
        <BasicTabs
          tabs={tabs}
          status={status}
          setStatus={(v) => {
            setStatus(v);
            setPagination({ pageIndex: 0, pageSize: 10 });
          }}
        />
      </MDBox>
      <TableCard>
        <MDBox display="flex" justifyContent="space-between" alignItems="flex-start" margin="10px">
          <MDBox display="flex">
            <MDButton
              variant="gradient"
              color="info"
              disabled={!addPermission}
              onClick={() => navigate("/setup/sku/add")}
            >
              <Icon>add</Icon>&nbsp;SKU
            </MDButton>
            {/* <MDButton
              variant="gradient"
              color="info"
              sx={{ marginLeft: "10px" }}
              disabled={!addPermission}
              onClick={() => navigate("/setup/sku/kit/add")}
            >
              <Icon>add</Icon>&nbsp;SKU&nbsp;Kit
            </MDButton> */}
            <MDButton
              variant="gradient"
              color={active ? "error" : "success"}
              sx={{ marginLeft: "10px" }}
              onClick={onBatch}
              disabled={selectedIds.length === 0 || !deactivatePermission}
            >
              {active ? "Deactivate SKU" : "Activate SKU"}
            </MDButton>
            {active && (
              <MDButton
                variant="gradient"
                color="primary"
                sx={{ marginLeft: "10px" }}
                onClick={onBatchDelete}
                disabled={data.length === 0 || !deactivatePermission}
              >
                Deactivate All SKU (Out Of Stock)
              </MDButton>
            )}
          </MDBox>
          <MDBox display="flex">
            <MDButton
              variant="outlined"
              color="info"
              disabled={!addPermission}
              onClick={() => navigate("/setup/sku/upload")}
            >
              Upload&nbsp;<Icon>upload</Icon>
            </MDButton>
            {/* <MDButton
              variant="outlined"
              color="info"
              sx={{ marginLeft: "10px" }}
              disabled={!addPermission}
              onClick={() => navigate("/setup/sku/kit/upload")}
            >
              upload&nbsp;Kit<Icon>upload</Icon>
            </MDButton> */}
            <MDButton
              variant="outlined"
              color="dark"
              sx={{ marginLeft: "10px" }}
              onClick={() => {
                exportMutation.mutate({ customerCode, active, columnFilters });
              }}
            >
              export&nbsp;<Icon>download</Icon>
            </MDButton>
            <MDButton
              variant="gradient"
              color="warning"
              sx={{ marginLeft: "10px" }}
              disabled={!addPermission}
              onClick={() => navigate("/setup/sku/packtype-weight/upload")}
            >
              Edit Pack Type Info
            </MDButton>
          </MDBox>
        </MDBox>
        <ServerTable
          data={data}
          columns={columns}
          onSelectedRowsChange={handleChange}
          {...tableProps}
        />
      </TableCard>
    </ListLayout>
  );
}

export default SKUList;
